import { useState, useCallback } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import AdminNotePanel from '@/components/common/AdminNotePanel'
import {
  BookOpen,
  FileText,
  Video,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Layers,
  GraduationCap,

  LayoutDashboard,
  PenTool,
  FolderTree,
  UsersRound,
  UserPlus,
  UserCog,
  ClipboardCheck,
  StickyNote,
  PlayCircle,
  DollarSign,
  Presentation,
  Zap,
  BookMarked,
  Clapperboard,
  Briefcase,
  Mail,
  Award,
  History,
  TrendingUp
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type NavItem = { name: string; href: string; icon: LucideIcon }
type NavSection = { section: string; icon: LucideIcon; items: NavItem[] }
type NavEntry = NavItem | NavSection

function isSection(entry: NavEntry): entry is NavSection {
  return 'section' in entry
}

export default function AdminLayout() {
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  const navigation: NavEntry[] = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    {
      section: 'Curso',
      icon: BookMarked,
      items: [
        { name: 'Niveles', href: '/admin/levels', icon: FolderTree },
        { name: 'Cohortes', href: '/admin/cohorts', icon: UsersRound },
        { name: 'Unidades', href: '/admin/units', icon: Layers },
        { name: 'Lecciones', href: '/admin/lessons', icon: BookOpen },
        { name: 'Contenidos', href: '/admin/contents', icon: FileText },
        { name: 'Ejercicios', href: '/admin/exercises', icon: PenTool },
        { name: 'Examenes', href: '/admin/exams', icon: GraduationCap },
      ],
    },
    {
      section: 'Talleres',
      icon: Clapperboard,
      items: [
        { name: 'Talleres', href: '/admin/workshops', icon: Video },
        { name: 'Material talleres', href: '/admin/workshop-content', icon: Presentation },
        { name: 'Talleres grabados', href: '/admin/recorded-workshops', icon: PlayCircle },
        { name: 'Asistencia', href: '/admin/attendance', icon: ClipboardCheck },
      ],
    },
    {
      section: 'Usuarios',
      icon: Users,
      items: [
        { name: 'Crear alumnos', href: '/admin/students/new', icon: UserPlus },
        { name: 'Crear profesores', href: '/admin/teachers/new', icon: UserCog },
        { name: 'Lista usuarios', href: '/admin/users', icon: Users },
      ],
    },
    {
      section: 'Gestion',
      icon: Briefcase,
      items: [
        { name: 'Avance alumnos', href: '/admin/progress', icon: TrendingUp },
        { name: 'Notas', href: '/admin/notes', icon: StickyNote },
        { name: 'Historial cambios', href: '/admin/edit-history', icon: History },
        { name: 'Flujos', href: '/admin/flows', icon: Zap },
        { name: 'Certificados', href: '/admin/certificates', icon: Award },
        { name: 'Ventas', href: '/admin/sales', icon: DollarSign },
        { name: 'Enviar Correo', href: '/admin/email', icon: Mail },
      ],
    },
  ]

  const isActive = (href: string) => {
    if (href === '/admin/dashboard') return location.pathname === '/admin/dashboard'
    return location.pathname.startsWith(href)
  }

  const isSectionActive = (entry: NavSection) =>
    entry.items.some((item) => isActive(item.href))

  const isSectionOpen = useCallback(
    (section: string, entry: NavSection) => {
      if (openSections[section] !== undefined) return openSections[section]
      return isSectionActive(entry)
    },
    [openSections, location.pathname]
  )

  const toggleSection = (section: string) =>
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/50 to-purple-50/50">
      {/* Sidebar movil */}
      <div className={cn(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div
          className="fixed inset-0 bg-black/50"
          role="button"
          tabIndex={0}
          aria-label="Cerrar menú"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setSidebarOpen(false) }}
        />
        <div className="fixed inset-y-0 left-0 w-64 bg-white/70 backdrop-blur-xl border-r border-white/40 shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-indigo-100/60">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <span className="text-xl font-bold text-gray-800">
                Nomad<span className="text-[#EF4135]">Lexis</span>
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-indigo-50 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-3 space-y-0.5 overflow-y-auto max-h-[calc(100vh-64px)]">
            {navigation.map((entry) => {
              if (!isSection(entry)) {
                return (
                  <Link
                    key={entry.name}
                    to={entry.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm',
                      isActive(entry.href)
                        ? 'bg-indigo-500/10 text-indigo-700 font-medium border border-indigo-200/50'
                        : 'hover:bg-white/60 text-gray-600'
                    )}
                  >
                    <entry.icon className={cn('w-4.5 h-4.5 shrink-0', isActive(entry.href) ? 'text-indigo-500' : 'text-gray-400')} />
                    <span>{entry.name}</span>
                  </Link>
                )
              }
              const open = isSectionOpen(entry.section, entry)
              return (
                <div key={entry.section}>
                  <button
                    onClick={() => toggleSection(entry.section)}
                    className={cn(
                      'flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all text-sm',
                      isSectionActive(entry) ? 'text-indigo-700 font-medium' : 'text-gray-500 hover:bg-white/60 hover:text-gray-700'
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <entry.icon className={cn('w-4.5 h-4.5 shrink-0', isSectionActive(entry) ? 'text-indigo-500' : 'text-gray-400')} />
                      <span>{entry.section}</span>
                    </span>
                    <ChevronDown className={cn('w-4 h-4 transition-transform', open ? '' : '-rotate-90')} />
                  </button>
                  {open && (
                    <div className="ml-4 pl-3 border-l border-indigo-100/60 space-y-0.5 mt-0.5">
                      {entry.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm',
                            isActive(item.href)
                              ? 'bg-indigo-500/10 text-indigo-700 font-medium border border-indigo-200/50'
                              : 'hover:bg-white/60 text-gray-500'
                          )}
                        >
                          <item.icon className={cn('w-4 h-4 shrink-0', isActive(item.href) ? 'text-indigo-500' : 'text-gray-400')} />
                          <span>{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Sidebar desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-60 lg:flex-col bg-white/60 backdrop-blur-2xl border-r border-white/50 shadow-[0_0_40px_-10px_rgba(99,102,241,0.15)]">
        {/* Brand header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-indigo-100/60">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-indigo-500" />
            <span className="text-lg font-bold text-gray-800">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>
        </div>

        {/* Admin badge */}
        <div className="px-4 py-3 border-b border-indigo-100/60">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50/80 rounded-lg border border-indigo-100/50">
            <div className="w-2 h-2 rounded-full bg-indigo-500" />
            <span className="text-xs font-medium text-indigo-600 tracking-wide uppercase">
              Administrador
            </span>
          </div>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
          {navigation.map((entry) => {
            if (!isSection(entry)) {
              return (
                <Link
                  key={entry.name}
                  to={entry.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm',
                    isActive(entry.href)
                      ? 'bg-indigo-500/10 text-indigo-700 font-semibold border border-indigo-200/60 shadow-sm'
                      : 'hover:bg-white/80 text-gray-600 hover:text-gray-800'
                  )}
                >
                  <entry.icon className={cn('w-[18px] h-[18px] shrink-0', isActive(entry.href) ? 'text-indigo-500' : 'text-gray-400')} />
                  <span>{entry.name}</span>
                </Link>
              )
            }
            const open = isSectionOpen(entry.section, entry)
            return (
              <div key={entry.section} className="mt-1">
                <button
                  onClick={() => toggleSection(entry.section)}
                  className={cn(
                    'flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all text-sm',
                    isSectionActive(entry) ? 'text-indigo-700 font-semibold' : 'text-gray-500 hover:bg-white/80 hover:text-gray-700'
                  )}
                >
                  <span className="flex items-center gap-3">
                    <entry.icon className={cn('w-[18px] h-[18px] shrink-0', isSectionActive(entry) ? 'text-indigo-500' : 'text-gray-400')} />
                    <span>{entry.section}</span>
                  </span>
                  <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', open ? '' : '-rotate-90')} />
                </button>
                {open && (
                  <div className="ml-4 pl-3 border-l border-indigo-100/60 space-y-0.5 mt-0.5 mb-1">
                    {entry.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all text-sm',
                          isActive(item.href)
                            ? 'bg-indigo-500/10 text-indigo-700 font-semibold border border-indigo-200/60 shadow-sm'
                            : 'hover:bg-white/80 text-gray-500 hover:text-gray-700'
                        )}
                      >
                        <item.icon className={cn('w-4 h-4 shrink-0', isActive(item.href) ? 'text-indigo-500' : 'text-gray-400')} />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Footer del sidebar */}
        <div className="px-3 py-3 border-t border-indigo-100/60">
          <Link
            to="/admin/settings"
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm',
              location.pathname === '/admin/settings'
                ? 'bg-indigo-500/10 text-indigo-700 font-semibold border border-indigo-200/60'
                : 'hover:bg-white/80 text-gray-500 hover:text-gray-700'
            )}
          >
            <Settings className="w-[18px] h-[18px]" />
            <span>Configuración</span>
          </Link>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="lg:pl-60">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/60 backdrop-blur-xl border-b border-indigo-100/40 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Boton menu movil */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-indigo-50 lg:hidden"
            >
              <Menu className="w-5 h-5 text-indigo-500" />
            </button>

            {/* Breadcrumb */}
            <div className="hidden lg:flex items-center gap-1 text-sm">
              {location.pathname.split('/').filter(Boolean).map((part, i, arr) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <span className="text-indigo-300">/</span>}
                  <span className={i === arr.length - 1 ? 'text-indigo-600 font-medium' : 'text-gray-400'}>
                    {part.charAt(0).toUpperCase() + part.slice(1)}
                  </span>
                </span>
              ))}
            </div>

            {/* Acciones del header */}
            <div className="flex items-center gap-2">
              {/* Menu de usuario */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-indigo-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-sm">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt=""
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">
                    {profile?.first_name}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden md:block" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      role="button"
                      tabIndex={0}
                      aria-label="Cerrar menú de usuario"
                      onClick={() => setUserMenuOpen(false)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setUserMenuOpen(false) }}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-indigo-100/50 z-50 overflow-hidden">
                      <div className="p-3 bg-indigo-50/50 border-b border-indigo-100/50">
                        <p className="font-medium text-gray-900">
                          {profile?.first_name} {profile?.last_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">
                          {profile?.email}
                        </p>
                        <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded text-xs font-medium">
                          <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
                          Administrador
                        </span>
                      </div>
                      <div className="p-1.5">
                        <Link
                          to="/admin/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 text-gray-700 text-sm"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Configuración</span>
                        </Link>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false)
                            signOut()
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-50 w-full text-left text-[#EF4135] text-sm"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Cerrar sesión</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Contenido de la pagina */}
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Panel flotante de notas del profesor */}
      <AdminNotePanel />
    </div>
  )
}
